/* global Sys, Core */
Sys.ns("Core");

Core.DeviceDetectionCodes = {
    WHITE: 0,
    GREY_OS: 1,
    GREY_OS_VERSION: 2,
    GREY_BROWSER: 3,
    GREY_BROWSER_VERSION: 4,
    BLACK_OS: 5,
    BLACK_OS_VERSION: 6,
    BLACK_BROWSER: 7,
    BLACK_BROWSER_VERSION: 8
};

/**
 * @typedef {Object} DeviceDetectionValidationResult
 * @property {string|null} preferredBrowser - The preferred browser to use or null if the OS was not identified.
 * @property {boolean} allowed - Whether or not the combination is allowed to enter.
 * @property {number} code - The result code listed in Core.DeviceDetectionCodes.
 */

/**
 * @namespace Core.DeviceDetectionService
 */
Core.DeviceDetectionService = (function() {
    var getVersion,
        findMatch,
        findMatchOnWhiteList,
        findMatchOnBlackList,
        validateCombination;

    /**
     * Gets the version number from the user agent.
     *
     * @private
     * @static
     * @param {string} userAgent - The user agent string.
     * @param {string} pattern - The regular expression pattern used to retrieve the version information.
     * @param {string} [separator] - The optional regular expression pattern used to retrieve the separate the version numbers.
     * @returns {string} The version.
     */
    getVersion = function(userAgent, pattern, separator) {
        var versionRegExp = new RegExp(pattern),
            matches = versionRegExp.exec(userAgent),
            version;

        if (!(matches && matches.length > 0)) {
            throw new Error("Could not find a matching version");
        }

        version = matches[1];

        if (separator) {
            version = version.replace(new RegExp(separator, "g"), ".");
        }

        if (!Core._DeviceDetectionUtils.isVersionNumber(version)) {
            throw new Error("The match found is not a valid version");
        }

        return version;
    };

    /**
     * Finds the list item that matches the user agent.
     *
     * @private
     * @static
     * @param {string} userAgent - The user agent string.
     * @param {Object} map - The map of items.
     * @param {Object} map.version - The version range to match against.
     * @param {string} [map.version.min] - The optional minimum version.
     * @param {string} [map.version.max] - The optional maximum version.
     * @param {Object} map.browsers - The optional map of browsers linked to this item.
     * @param {Object} definitions - The map of item definitions.
     * @param {string} definitions.matchPattern - The regular expression pattern used to identify the item.
     * @param {string} [definitions.excludePattern] - The regular expression pattern used to negate identification of the item.
     * @param {Object} definitions.version - The instructions on how to identify the version.
     * @param {string} definitions.version.matchPattern - The regular expression pattern used to retrieve the version information.
     * @param {string} [definitions.version.separator] - The optional regular expression pattern used to retrieve the separate the version numbers.
     * @returns {Object|null} Returns an object containing the preferredBrowser and available browsers or null if no match was found.
     */
    findMatch = function(userAgent, map, definitions) {
        var names = Object.keys(map),
            numNames = names.length,
            item,
            definition,
            matcher,
            excluded,
            version,
            inRange,
            i;

        for (i = 0; i < numNames; i++) {
            item = map[names[i]];
            definition = definitions[names[i]];
            excluded = false;

            matcher = new RegExp(definition.matchPattern);

            if (definition.excludePattern) {
                excluded = (new RegExp(definition.excludePattern)).test(userAgent);
            }

            if (matcher.test(userAgent) && !excluded) {
                if (item.version) {
                    version = getVersion(userAgent, definition.version.matchPattern, definition.version.separator);
                    inRange = Core._DeviceDetectionUtils.isInRange(version, item.version.min, item.version.max);
                }
                else {
                    inRange = true;
                }

                return {
                    name: names[i],
                    inRange: inRange
                };
            }
        }

        return null;
    };

    /**
     * Tries to find a match for the user agent in the white list.
     *
     * @private
     * @static
     * @param {string} userAgent - The user agent string.
     * @param {Object} configuration - The device detection configuration.
     * @param {Object} configuration.definitions - The OS and browser definitions.
     * @param {Object} configuration.definitions.operatingSystems - The OS definitions.
     * @param {Object} configuration.definitions.browsers - The browser definitions.
     * @param {Object} configuration.ruleSets - The rule sets containing the white and black lists.
     * @param {Object} [configuration.ruleSets.white] - The optional white list rules.
     * @param {Object} [configuration.ruleSets.black] - The optional black list rules.
     * @returns {Object} The properties identified for a partial or full match.
     */
    findMatchOnWhiteList = function(userAgent, configuration) {
        var operatingSystems = configuration.definitions.operatingSystems,
            list = configuration.ruleSets.white || {},
            osMatch = findMatch(userAgent, list, operatingSystems),
            browserMatch,
            browsers,
            result = {};

        if (osMatch) {
            result.allowed = true;
            result.preferredBrowser = operatingSystems[osMatch.name].preferredBrowser;

            if (!osMatch.inRange) {
                result.code = Core.DeviceDetectionCodes.GREY_OS_VERSION;
                return result;
            }

            browsers = list[osMatch.name].browsers;
            browserMatch = findMatch(userAgent, browsers || {}, configuration.definitions.browsers);

            if (!browsers) {
                result.code = Core.DeviceDetectionCodes.WHITE;
            }
            else if (browserMatch) {
                if (!browserMatch.inRange) {
                    result.code = Core.DeviceDetectionCodes.GREY_BROWSER_VERSION;
                }
                else {
                    result.code = Core.DeviceDetectionCodes.WHITE;
                }
            }
            else {
                result.code = Core.DeviceDetectionCodes.GREY_BROWSER;
            }
        }

        return result;
    };

    /**
     * Tries to find a match for the user agent in the black list.
     *
     * @private
     * @static
     * @param {string} userAgent - The user agent string.
     * @param {Object} configuration - The device detection configuration.
     * @param {Object} configuration.definitions - The OS and browser definitions.
     * @param {Object} configuration.definitions.operatingSystems - The OS definitions.
     * @param {Object} configuration.definitions.browsers - The browser definitions.
     * @param {Object} configuration.ruleSets - The rule sets containing the white and black lists.
     * @param {Object} [configuration.ruleSets.white] - The optional white list rules.
     * @param {Object} [configuration.ruleSets.black] - The optional black list rules.
     * @returns {Object} The properties identified for a partial or full match.
     */
    findMatchOnBlackList = function(userAgent, configuration) {
        var operatingSystem = configuration.definitions.operatingSystems,
            list = configuration.ruleSets.black || {},
            osMatch = findMatch(userAgent, list, operatingSystem),
            os,
            browsers,
            browserMatch,
            browser,
            result = {};

        if (osMatch && osMatch.inRange) {
            os = list[osMatch.name];
            browsers = os.browsers;

            browserMatch = findMatch(userAgent, browsers || {}, configuration.definitions.browsers);

            if (browserMatch && browserMatch.inRange) {
                browser = browsers[browserMatch.name];

                result.preferredBrowser = operatingSystem[osMatch.name].preferredBrowser;
                result.allowed = false;

                if (!browser.version) {
                    result.code = Core.DeviceDetectionCodes.BLACK_BROWSER;
                }
                else {
                    result.code = Core.DeviceDetectionCodes.BLACK_BROWSER_VERSION;
                }
            }
            else if (!os.version) {
                result.allowed = false;
                result.code = Core.DeviceDetectionCodes.BLACK_OS;
            }
            else if (!browsers) {
                result.allowed = false;
                result.code = Core.DeviceDetectionCodes.BLACK_OS_VERSION;
            }
        }

        return result;
    };

    /**
     * Validates the OS and browser combination listed in the user agent against
     * the provided device detection configuration.
     *
     * @private
     * @static
     * @param {string} userAgent - The user agent string.
     * @param {Object} configuration - The device detection configuration.
     * @param {Object} configuration.definitions - The OS and browser definitions.
     * @param {Object} configuration.definitions.operatingSystems - The OS definitions.
     * @param {Object} configuration.definitions.browsers - The browser definitions.
     * @param {Object} configuration.ruleSets - The rule sets containing the white and black lists.
     * @param {Object} [configuration.ruleSets.white] - The optional white list rules.
     * @param {Object} [configuration.ruleSets.black] - The optional black list rules.
     * @returns {DeviceDetectionValidationResult} The result of the validation.
     */
    validateCombination = function(userAgent, configuration) {
        var result = {
            preferredBrowser: null,
            allowed: true,
            code: Core.DeviceDetectionCodes.GREY_OS
        };

        Sys.applyProperties(result, findMatchOnWhiteList(userAgent, configuration));
        Sys.applyProperties(result, findMatchOnBlackList(userAgent, configuration));

        return result;
    };

    return {

        /**
         * Validates the OS and browser combination listed in the user agent against
         * the provided device detection configuration.
         *
         * @static
         * @memberof Core.DeviceDetectionService
         * @param {string} userAgent - The user agent string.
         * @param {Object} configuration - The device detection configuration.
         * @param {Object} configuration.definitions - The OS and browser definitions.
         * @param {Object} configuration.definitions.operatingSystems - The OS definitions.
         * @param {Object} configuration.definitions.browsers - The browser definitions.
         * @param {Object} configuration.ruleSets - The rule sets containing the white and black lists.
         * @param {Object} [configuration.ruleSets.white] - The optional white list rules.
         * @param {Object} [configuration.ruleSets.black] - The optional black list rules.
         * @returns {DeviceDetectionValidationResult} The result of the validation.
         */
        validate: function(userAgent, configuration) {
            if (typeof userAgent !== "string") {
                throw new Error("Could not validate the device since the user agent was not a string");
            }

            if (!Sys.isObj(configuration)) {
                throw new Error("Could not validate the device since the configuration was not an object");
            }

            return validateCombination(userAgent, configuration);
        }
    };
}());
